import React from "react";
import { Image, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { FertilityTestingStackScreenProps } from "../FertilityTestingNavigator";
import { useFertilityTesting } from "../FertilityTestingContext";
import { transformFertilityTestingStateToRequest } from "../transformFertilityTestingStateToRequest";
import {
  BaseCompleteScreen,
  navigateToNextSection,
  getNextSectionButtonLabel,
} from "@louise/medical-history/components/BaseCompleteScreen";
import { useQueuedPatientUpdate } from "@src/packages/patients/sync/usePatientSync";
import { useTranslations } from "@src/packages/i18n";

const fertilityTestingIllustration =
  require("@src/apps/moon/assets/images/you-icons/test-tubes.png") as number;

function FertilityTestingIllustration() {
  return (
    <View
      accessible
      accessibilityLabel="Testing records"
      testID="medical-history-fertility-testing-illustration"
      style={{
        width: 132,
        height: 148,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 4,
      }}
    >
      <Image
        source={fertilityTestingIllustration}
        resizeMode="contain"
        style={{ width: 64, height: 142 }}
      />
    </View>
  );
}

export function FertilityTestingCompleteScreen() {
  const t = useTranslations();
  const { state } = useFertilityTesting();

  const selectedTypes = state.selectedTypes ?? [];
  const selectedTypeLabels = selectedTypes.map((type) =>
    t(`med_history.fertility.testing.type.${type}`),
  );
  const recap = selectedTypeLabels.length
    ? t("med_history.fertility.testing.complete_recap", {
        params: { types: selectedTypeLabels.join(", ") },
      })
    : t("med_history.fertility.testing.complete_recap_empty");
  const personalizedMessage = state.uploadedFiles.length
    ? t(
        state.uploadedFiles.length === 1
          ? "med_history.fertility.testing.complete_insight_file"
          : "med_history.fertility.testing.complete_insight_files",
        {
          params: { count: String(state.uploadedFiles.length) },
        },
      )
    : selectedTypes.includes("semen")
      ? t("med_history.fertility.testing.complete_insight_semen")
      : selectedTypes.includes("blood")
        ? t("med_history.fertility.testing.complete_insight_blood")
        : t("med_history.fertility.testing.complete_insight_default");

  const navigation =
    useNavigation<
      FertilityTestingStackScreenProps<"FertilityTestingComplete">["navigation"]
    >();

  const { mutateAsync: updatePatient, isPending: isUpdatingPatient } =
    useQueuedPatientUpdate();

  const handleNext = async () => {
    try {
      const payload = transformFertilityTestingStateToRequest(state);

      await updatePatient(payload);

      navigateToNextSection(navigation, "fertilityTesting");
    } catch {}
  };

  return (
    <BaseCompleteScreen
      sectionLabel={t("med_history.fertility.testing.complete_section_label")}
      showSectionLabel={false}
      title={t("med_history.fertility.testing.complete_title")}
      subtitle={t("med_history.fertility.testing.complete_subtitle")}
      secondarySubtitle={t(
        "med_history.fertility.testing.complete_return_hint",
      )}
      recap={recap}
      showRecapLabel={false}
      personalizedMessage={personalizedMessage}
      showInsightLabel={false}
      illustration={<FertilityTestingIllustration />}
      buttonLabel={getNextSectionButtonLabel(t, "fertilityTesting")}
      isSubmitting={isUpdatingPatient}
      onFinish={handleNext}
    />
  );
}

export default FertilityTestingCompleteScreen;
